/**
 * ResolvedAccountBadge — shows enriched account info when account_uuid is resolved.
 *
 * Fetches quick facts for the given account UUID and displays a compact
 * info line with the canonical name, city/state, and last visit info.
 * Links to the account page in the copilot.
 */

import { useQuickFacts } from "../hooks/useQuickFacts";
import { MapPin, ExternalLink } from "lucide-react";
import { cn } from "../lib/utils";
import { formatShortDate } from "../../utils/dateFormatters";

interface ResolvedAccountBadgeProps {
  accountUuid: string;
  className?: string;
}

export function ResolvedAccountBadge({
  accountUuid,
  className,
}: ResolvedAccountBadgeProps) {
  const { data, loading } = useQuickFacts(accountUuid);

  if (loading) {
    return (
      <div className={cn("animate-pulse text-xs text-text-muted", className)}>
        Loading account info...
      </div>
    );
  }

  if (!data) return null;

  const location = [data.city, data.state].filter(Boolean).join(", ");
  const lastVisit = data.visit_frequency?.last_visit_date;
  const lastVisitLabel = lastVisit ? `Last visited ${formatShortDate(lastVisit)}` : null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-blue-50 px-2.5 py-1.5 text-xs dark:bg-blue-950/30",
        className,
      )}
    >
      <span className="font-medium text-text-primary">{data.company_name}</span>

      {location && (
        <span className="inline-flex items-center gap-1 text-text-secondary">
          <MapPin className="h-3 w-3" />
          {location}
        </span>
      )}

      {data.industry_type && (
        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium capitalize text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
          {data.industry_type.replace("_", " ")}
        </span>
      )}

      {lastVisitLabel && (
        <span className="text-text-muted">{lastVisitLabel}</span>
      )}

      {data.has_sage_link && data.customer_slug && (
        <a
          href={`/portal/customers/${data.customer_slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-brand-primary hover:underline"
        >
          Portal
          <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
    </div>
  );
}
